/*!
 * AudoChat - Realtime Utility
 * Handles Supabase Realtime subscriptions
 */

import { supabase } from '../lib/supabase';

/**
 * Subscribe to changes in a table
 * @param {string} table - Table name
 * @param {function} callback - Callback function to handle changes
 * @param {object} options - Additional options (event, filter, etc.)
 * @returns {object} Subscription object
 */
export function subscribeToTable(table, callback, options = {}) {
  const {
    event = '*', // INSERT, UPDATE, DELETE, or *
    filter = null, // e.g., { column: 'conversation_id', value: 123 }
    schema = 'public',
  } = options;

  const channelName = `${table}-changes-${Date.now()}`;
  
  try {
    const channel = supabase.channel(channelName, {
      config: {
        broadcast: { self: true },
        presence: { key: '' }
      }
    });

    // Build the postgres_changes configuration
    const postgresConfig = {
      event,
      schema,
      table,
    };

    // Build filter in the correct format for Supabase Realtime
    // Filter should be in format: 'column=eq.value' (no quotes for numbers)
    if (filter) {
      // If filter is a string, use it directly
      if (typeof filter === 'string') {
        postgresConfig.filter = filter;
      } 
      // If filter is an object with column and value, build the filter string
      else if (filter.column && filter.value !== undefined) {
        // For Supabase Realtime, values should be passed without quotes
        // Numbers and UUIDs should be passed as-is, strings should be passed as-is too
        const value = filter.value;
        postgresConfig.filter = `${filter.column}=eq.${value}`;
      }
    }

    const subscription = channel
      .on(
        'postgres_changes',
        postgresConfig,
        (payload) => {
          console.log(`Change received in ${table}:`, payload);
          try {
            callback(payload);
          } catch (error) {
            console.error(`Error in callback for ${table}:`, error);
          }
        }
      )
      .subscribe((status, err) => {
        const filterStr = typeof filter === 'string' 
          ? filter 
          : filter?.column 
            ? `${filter.column}=eq.${filter.value}` 
            : '';
        console.log(`Subscription status for ${table}:`, status, filterStr ? `(filter: ${filterStr})` : '');
        if (status === 'SUBSCRIBED') {
          console.log(`✅ Subscribed to ${table} changes`);
        } else if (status === 'CHANNEL_ERROR') {
          console.error(`❌ Error subscribing to ${table} changes:`, err);
          // Log more details about the error
          if (err) {
            console.error('Error details:', {
              message: err.message,
              status: err.status,
              error: err.error
            });
          }
          
          // Check if it's a CORS error
          const isCorsError = !err || err.message?.includes('CORS') || 
                            err.message?.includes('Access-Control') ||
                            err.message?.includes('blocked');
          
          if (isCorsError) {
            console.error(`🚫 CORS Error: WebSocket connection blocked. Please configure CORS in Supabase server to allow requests from ${window.location.origin}`);
            console.error(`📖 See CORS_FIX_GUIDE.md for instructions on how to fix this.`);
          } else {
            // Note: Realtime might not be enabled for this table in Supabase
            console.warn(`⚠️ Realtime may not be enabled for table "${table}" in Supabase. The app will continue to work, but real-time updates may not be available.`);
          }
        } else if (status === 'TIMED_OUT') {
          console.warn(`⏱️ Timeout subscribing to ${table} changes`);
        } else if (status === 'CLOSED') {
          console.log(`🔒 Channel closed for ${table}`);
        }
      });

    return subscription;
  } catch (error) {
    console.error(`Failed to create subscription for ${table}:`, error);
    // Return a mock subscription object to prevent errors
    return {
      unsubscribe: () => {},
      channel: null
    };
  }
}

/**
 * Subscribe to conversations changes
 */
export function subscribeToConversations(callback) {
  return subscribeToTable('conversations', callback, {
    event: '*',
  });
}

/**
 * Subscribe to messages changes
 */
export function subscribeToMessages(conversationId, callback) {
  // Convert conversationId to string to ensure consistent formatting
  const filterValue = String(conversationId);
  const filter = `conversation_id=eq.${filterValue}`;
  
  // Create a unique channel name for this subscription
  const channelName = `messages-${conversationId}-${Date.now()}`;
  
  try {
    const channel = supabase.channel(channelName, {
      config: {
        broadcast: { self: true },
        presence: { key: '' }
      }
    });

    // Subscribe to INSERT events
    channel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: filter,
      },
      (payload) => {
        console.log('Message INSERT received:', payload);
        try {
          callback(payload);
        } catch (error) {
          console.error('Error in messages INSERT callback:', error);
        }
      }
    );

    // Subscribe to UPDATE events
    channel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: filter,
      },
      (payload) => {
        console.log('Message UPDATE received:', payload);
        try {
          callback(payload);
        } catch (error) {
          console.error('Error in messages UPDATE callback:', error);
        }
      }
    );

    // Subscribe to DELETE events
    channel.on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'messages',
        filter: filter,
      },
      (payload) => {
        console.log('Message DELETE received:', payload);
        try {
          callback(payload);
        } catch (error) {
          console.error('Error in messages DELETE callback:', error);
        }
      }
    );

    // Subscribe to the channel
    channel.subscribe((status, err) => {
      console.log(`Subscription status for messages (conversation ${conversationId}):`, status);
      if (status === 'SUBSCRIBED') {
        console.log(`✅ Subscribed to messages changes for conversation ${conversationId}`);
      } else if (status === 'CHANNEL_ERROR') {
        console.error(`❌ Error subscribing to messages changes:`, err);
        if (err) {
          console.error('Error details:', {
            message: err.message,
            status: err.status,
            error: err.error
          });
        }
        
        // Check if it's a CORS error
        const isCorsError = !err || err.message?.includes('CORS') || 
                          err.message?.includes('Access-Control') ||
                          err.message?.includes('blocked');
        
        if (isCorsError) {
          console.error(`🚫 CORS Error: WebSocket connection blocked. Please configure CORS in Supabase server to allow requests from ${window.location.origin}`);
          console.error(`📖 See CORS_FIX_GUIDE.md for instructions on how to fix this.`);
        } else {
          console.warn(`⚠️ Realtime may not be enabled for table "messages" in Supabase. The app will continue to work, but real-time updates may not be available.`);
        }
      } else if (status === 'TIMED_OUT') {
        console.warn(`⏱️ Timeout subscribing to messages changes`);
      } else if (status === 'CLOSED') {
        console.log(`🔒 Channel closed for messages`);
      }
    });

    return channel;
  } catch (error) {
    console.error(`Failed to create messages subscription:`, error);
    // Return a mock subscription object to prevent errors
    return {
      unsubscribe: () => {},
      channel: null
    };
  }
}

/**
 * Subscribe to ALL messages changes (for global conversation list updates)
 */
export function subscribeToAllMessages(callback) {
  return subscribeToTable('messages', callback, {
    event: '*',
  });
}

/**
 * Subscribe to users changes
 */
export function subscribeToUsers(callback) {
  return subscribeToTable('users', callback, {
    event: '*',
  });
}

/**
 * Subscribe to customers changes
 */
export function subscribeToCustomers(callback) {
  return subscribeToTable('customers', callback, {
    event: '*',
  });
}

/**
 * Unsubscribe from a channel
 */
export function unsubscribe(subscription) {
  if (subscription) {
    try {
      // Check if subscription has a channel property
      if (subscription.channel) {
        supabase.removeChannel(subscription.channel);
      } else {
        // Try to remove the subscription directly
        supabase.removeChannel(subscription);
      }
    } catch (error) {
      console.warn('Error unsubscribing:', error);
      // Ignore errors during unsubscribe - subscription may already be closed
    }
  }
}

export default {
  subscribeToTable,
  subscribeToConversations,
  subscribeToMessages,
  subscribeToUsers,
  subscribeToCustomers,
  unsubscribe,
};

